import { FC, useCallback, useMemo, MouseEvent, useState } from 'react';
import { Edit } from '@oresundsbron/icons';
import {
  Box,
  Button,
  ButtonProps,
  LoadingButton,
} from '@oresundsbron/bridge-ui';
import { useStepForm } from '@oresundsbron/use-form';
import { pipe } from 'fp-ts/function';
import { match } from 'fp-ts/lib/boolean';
import { Task } from 'fp-ts/Task';

export const FormButton: FC<
  Omit<ButtonProps, 'variant' | 'children' | 'onClick'> & {
    onClick: () => Task<boolean>;
    namespace: string;
    odd?: boolean;
    loadingLabel: string;
  }
> = ({ onClick, namespace, odd, loadingLabel, ...props }) => {
  const [loading, toggleLoading] = useState(false);
  const { labels, isLast, nextStep } = useStepForm();

  const label = useMemo(
    () =>
      pipe(
        isLast(namespace),
        match(
          () => labels.nextLabel,
          () => labels.submitLabel
        )
      ),
    [isLast, labels.nextLabel, labels.submitLabel, namespace]
  );

  const internalOnClick = useCallback(async () => {
    toggleLoading(true);
    const res = await onClick()();
    toggleLoading(false);
    if (!res) {
      return;
    }

    nextStep();
  }, [nextStep, onClick]);

  return (
    <Box className="col-span-full mt-2 flex justify-end">
      <LoadingButton
        {...props}
        onClick={internalOnClick}
        isLoading={loading}
        loadingLabel={loadingLabel}
        type={isLast(namespace) ? 'submit' : 'button'}
        intent="solid"
      >
        {label}
      </LoadingButton>
    </Box>
  );
};

export const EditButton: FC<
  Omit<ButtonProps, 'variant' | 'children' | 'onClick'> & { namespace: string }
> = ({ namespace, ...rest }) => {
  const { labels, isLast, enableStep } = useStepForm();

  if (isLast(namespace)) {
    return null;
  }

  return (
    <>
      <Button
        {...rest}
        onClick={() => enableStep(namespace)}
        intent="text"
        color="neutral"
        type="button"
        startIcon={<Edit />}
        className="ml-auto sm:hidden"
        aria-label={labels.editLabel}
        iconSize="lg"
      />

      <Button
        {...rest}
        onClick={() => enableStep(namespace)}
        intent="text"
        type="button"
        color="neutral"
        startIcon={<Edit />}
        className="ml-auto hidden sm:flex"
      >
        {labels.editLabel}
      </Button>
    </>
  );
};
